import React, { useState, useRef, useEffect } from "react";
import forwardArrow from "../../../assets/icons/forward.svg";
import Card from "./card";
import accountService from "../../../api/services/accountService";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Schedule from "../../../assets/icons/Schedule.svg";
import { useDispatch, useSelector } from "react-redux";
import { playSong } from "../../common/actions/common.actions";
import { selectLoginData } from "../login/login.actions";
import { FiSearch } from "react-icons/fi";
import { BiSolidCalendar } from "react-icons/bi";
import whiteSchedule from "../../../assets/icons/whiteSchedule.svg";
import { IoIosArrowBack } from "react-icons/io";
import { MdAccountBalanceWallet } from "react-icons/md";

const Account = ({ closeSidebar, setIsLoading }) => {
  const [transactionData, setTransactionData] = useState([]);
  const today = new Date();
  const [dateRange, setDateRange] = useState([today, today]);
  const [startDate, endDate] = dateRange;
  const [transactionsAvailable, setTransactionsAvailable] = useState(true);
  const { userDetails } = useSelector(selectLoginData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const datePickerRef = useRef(null);

  const handleSongClick = async () => {
    dispatch(playSong());
  };

  const handleClick = () => {
    handleSongClick();
    navigate("/dashboard");
  };

  const handleDateChange = (update) => {
    setDateRange(update);
  };

  const handleSearchClick = () => {
    fetchData(startDate, endDate);
  };

  const fetchData = async (start, end) => {
    if (start && end) {
      setIsLoading(true);
      const formattedStartDate = moment(start).format("YYYY-MM-DD");
      const formattedEndDate = moment(end).format("YYYY-MM-DD");
      try {
        const response = await accountService.getTransactionsForUser({
          userId: Number(userDetails.userId),
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        });
        const data = await response.json();

        if (data?.userTransactions) {
          const filteredData = data?.userTransactions.filter(
            (transaction) =>
              moment(transaction?.date).isSameOrAfter(start, "day") &&
              moment(transaction?.date).isSameOrBefore(end, "day")
          );
          setTransactionData(filteredData);
          setTransactionsAvailable(filteredData.length > 0);
        } else {
          setTransactionsAvailable(false);
        }
      } catch (error) {
        console.error("API request error:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(startDate, endDate);
  }, []);

  return (
    <div className="relative flex flex-col w-full h-[100dvh] overflow-hidden bg-gradient-to-br from-silver via-gray-100 to-silver">
      {/* Header */}
      <div className="relative z-10 border-b-2 shadow-2xl bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/40">
        <div className="flex items-center justify-between px-5 py-4">
          <button
            onClick={handleClick}
            className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
          >
            <IoIosArrowBack size={24} />
          </button>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 border-2 shadow-lg bg-gradient-to-br from-crimson via-red-primary to-crimson rounded-xl border-golden/40">
              <MdAccountBalanceWallet
                size={22}
                className="text-white drop-shadow-lg"
              />
            </div>
            <h3 className="text-lg font-bold leading-tight text-blue-primary">
              {t("statement")}
            </h3>
          </div>
          <div className="w-10"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 shadow-lg bg-gradient-to-r from-crimson via-golden to-crimson"></div>
      </div>

      {/* Main */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        {/* Date Range */}
        <div className="px-4 py-4 border-b-2 bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/20">
          <label className="block mb-2 text-sm font-semibold text-blue-primary">
            Select Date Range
          </label>
          <div className="flex items-center gap-3">
            <div
              onClick={() => datePickerRef.current?.setOpen(true)}
              className="flex items-center bg-white border-2 border-crimson/30 rounded-xl px-4 py-2.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex-1"
            >
              <BiSolidCalendar size={20} className="mr-3 text-blue-primary" />
              <span className="font-semibold text-blue-primary">
                {moment(startDate).format("DD-MM-YYYY")} -{" "}
                {moment(endDate).format("DD-MM-YYYY")}
              </span>
            </div>
            <button
              className="flex items-center justify-center w-12 h-12 text-white transition-all duration-300 border-2 shadow-lg bg-gradient-to-br from-crimson via-red-primary to-crimson hover:shadow-xl rounded-xl border-golden/40"
              onClick={handleSearchClick}
              title="Search Transactions"
            >
              <FiSearch size={20} />
            </button>
          </div>

          {/* DatePicker */}
          <DatePicker
            ref={datePickerRef}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            withPortal
            portalId="datepicker-portal"
            className="hidden"
          />
        </div>

        {/* Transaction List */}
        <div className="px-4 py-4">
          {transactionsAvailable ? (
            <div className="space-y-3">
              {transactionData?.map((data, index) => (
                <Card key={index} data={data} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[350px] bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl rounded-2xl border-2 border-crimson/30 shadow-2xl p-8">
              <div className="flex items-center justify-center w-16 h-16 mb-4 border-2 rounded-full bg-gradient-to-br from-crimson via-red-primary to-crimson border-golden/40">
                <MdAccountBalanceWallet size={32} className="text-white" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-blue-primary">
                No Transactions Found
              </h3>
              <p className="text-sm text-center text-blue-primary">
                {t("noTransaction")}
              </p>
              <p className="mt-1 text-sm text-center text-blue-primary/70">
                Try selecting a different date range
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;
